import React, { useState, useEffect } from 'react';
import { Modal, Spinner } from 'react-bootstrap';
import { FileText, Calendar, Calculator, CheckCircle, AlertTriangle } from 'lucide-react';
import { useParams } from 'react-router-dom';
import axios from '../../../../services/AxiosInstance';
import { getApiUrl } from '../../../../utils/ApiUtils';
import moment from 'moment';

const VatVerificationViewModal = ({ show, onHide, verificationId }) => {
    const { companyId } = useParams();
    const [verification, setVerification] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (show && verificationId) {
            setLoading(true);
            axios.get(getApiUrl(`${companyId}/verification/${verificationId}`), { withCredentials: true })
                .then(res => {
                    if (res.data) setVerification(res.data);
                })
                .catch(err => console.error('Error fetching verification:', err))
                .finally(() => setLoading(false));
        }
    }, [show, verificationId, companyId]);

    const formatCurrency = (val) =>
        new Intl.NumberFormat('sv-SE', { style: 'currency', currency: 'SEK', minimumFractionDigits: 2 }).format(Math.abs(parseFloat(val) || 0));

    if (!show) return null;

    const transactions = [...(verification?.transactions || [])].sort((a, b) => (a.account_number || '').localeCompare(b.account_number || ''));
    const debitSum = transactions.filter(t => t.transaction_type === 'debit').reduce((sum, t) => sum + parseFloat(t.amount || 0), 0);
    const creditSum = transactions.filter(t => t.transaction_type === 'credit').reduce((sum, t) => sum + parseFloat(t.amount || 0), 0);
    const isBalanced = Math.abs(debitSum - creditSum) <= 0.01;

    return (
        <Modal show={show} onHide={onHide} size="lg" centered>
            <Modal.Header closeButton className="border-0 pb-0">
                <Modal.Title className="h5">
                    <div className="d-flex align-items-center gap-2">
                        <FileText size={20} style={{ color: 'var(--primary)' }} />
                        Verifikat {verification?.series}{verification?.number}
                    </div>
                </Modal.Title>
            </Modal.Header>

            <Modal.Body className="px-4 py-3">
                <style>{`
                    .vv-section { background: #f8fafc; border-radius: 8px; padding: 1rem; margin-bottom: 1rem; border: 1px solid #e2e8f0; }
                    .vv-info-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)); gap: 0.75rem; }
                    .vv-info-card { background: white; border: 1px solid #e2e8f0; border-radius: 6px; padding: 0.6rem 0.75rem; }
                    .vv-info-label { font-size: 0.68rem; color: var(--secondary); text-transform: uppercase; letter-spacing: 0.03em; font-weight: 600; margin-bottom: 0.15rem; }
                    .vv-info-value { font-size: 0.85rem; font-weight: 500; color: #1e293b; }
                    .vv-table { background: white; border-radius: 6px; overflow: hidden; border: 1px solid #e2e8f0; }
                    .vv-table-header { background: #f8fafc; border-bottom: 1px solid #e2e8f0; padding: 0.5rem 0.75rem; font-size: 0.72rem; font-weight: 600; text-transform: uppercase; letter-spacing: 0.03em; color: var(--secondary); }
                    .vv-table-row { padding: 0.5rem 0.75rem; border-bottom: 1px solid #f1f5f9; font-size: 0.85rem; }
                    .vv-table-row:last-child { border-bottom: none; }
                    .vv-table-footer { background: #f1f5f9; padding: 0.5rem 0.75rem; font-weight: 600; font-size: 0.85rem; border-top: 1px solid #e2e8f0; }
                    .vv-amount { font-family: 'SF Mono', Monaco, Consolas, monospace; font-size: 0.8rem; }
                `}</style>

                {loading ? (
                    <div className="text-center py-4">
                        <Spinner animation="border" size="sm" />
                        <p className="mt-2 small text-muted">Laddar verifikat...</p>
                    </div>
                ) : verification ? (
                    <>
                        {/* Description */}
                        {verification.description && (
                            <div className="mb-3" style={{ fontSize: '0.9rem', color: '#475569' }}>
                                {verification.description}
                            </div>
                        )}

                        {/* Basic info */}
                        <div className="vv-section">
                            <div className="d-flex align-items-center mb-3">
                                <Calendar size={16} className="me-2" style={{ color: 'var(--secondary)' }} />
                                <h6 className="mb-0 fw-medium" style={{ fontSize: '0.85rem' }}>Grundinformation</h6>
                            </div>
                            <div className="vv-info-grid">
                                <div className="vv-info-card">
                                    <div className="vv-info-label">Datum</div>
                                    <div className="vv-info-value">{moment(verification.date).format('D MMMM YYYY')}</div>
                                </div>
                                <div className="vv-info-card">
                                    <div className="vv-info-label">Serie & Nummer</div>
                                    <div className="vv-info-value">{verification.series}{verification.number}</div>
                                </div>
                                <div className="vv-info-card">
                                    <div className="vv-info-label">Skapat</div>
                                    <div className="vv-info-value">{moment(verification.created_at).format('D MMM YYYY HH:mm')}</div>
                                </div>
                            </div>

                            {verification.notes && verification.notes !== 'review_step' && verification.notes !== 'record_step' && (
                                <div className="mt-3">
                                    <div className="vv-info-label mb-1">Anteckningar</div>
                                    <div className="vv-info-card">
                                        <div className="vv-info-value" style={{ whiteSpace: 'pre-wrap' }}>{verification.notes}</div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Transactions */}
                        <div className="vv-section">
                            <div className="d-flex align-items-center justify-content-between mb-3">
                                <div className="d-flex align-items-center">
                                    <Calculator size={16} className="me-2" style={{ color: 'var(--secondary)' }} />
                                    <h6 className="mb-0 fw-medium" style={{ fontSize: '0.85rem' }}>Transaktioner</h6>
                                </div>
                                <span className="d-inline-flex align-items-center gap-1" style={{ fontSize: '0.75rem', color: isBalanced ? 'var(--primary-dark)' : 'var(--bs-danger)' }}>
                                    {isBalanced ? <CheckCircle size={12} /> : <AlertTriangle size={12} />}
                                    {isBalanced ? 'Balanserad' : 'Obalanserad'}
                                </span>
                            </div>

                            <div className="vv-table">
                                <div className="vv-table-header d-flex">
                                    <div style={{ flex: 1, minWidth: '50px' }}>Konto</div>
                                    <div style={{ flex: 3 }}>Kontonamn</div>
                                    <div style={{ flex: 1, textAlign: 'right' }}>Debet</div>
                                    <div style={{ flex: 1, textAlign: 'right' }}>Kredit</div>
                                </div>

                                {transactions.map((t, i) => (
                                    <div key={i} className="vv-table-row d-flex align-items-center">
                                        <div style={{ flex: 1, minWidth: '50px' }}>
                                            <span style={{ fontFamily: 'monospace', fontWeight: 600, fontSize: '0.82rem' }}>{t.account_number}</span>
                                        </div>
                                        <div style={{ flex: 3, color: '#475569', fontSize: '0.82rem' }}>{t.account_name}</div>
                                        <div style={{ flex: 1, textAlign: 'right' }} className="vv-amount">
                                            {t.transaction_type === 'debit' ? (
                                                <span style={{ color: 'var(--primary-dark)', fontWeight: 600 }}>{formatCurrency(t.amount)}</span>
                                            ) : (
                                                <span style={{ color: '#cbd5e1' }}>—</span>
                                            )}
                                        </div>
                                        <div style={{ flex: 1, textAlign: 'right' }} className="vv-amount">
                                            {t.transaction_type === 'credit' ? (
                                                <span style={{ color: 'var(--primary)', fontWeight: 600 }}>{formatCurrency(t.amount)}</span>
                                            ) : (
                                                <span style={{ color: '#cbd5e1' }}>—</span>
                                            )}
                                        </div>
                                    </div>
                                ))}

                                <div className="vv-table-footer d-flex">
                                    <div style={{ flex: 1, minWidth: '50px' }}></div>
                                    <div style={{ flex: 3 }}>Summa</div>
                                    <div style={{ flex: 1, textAlign: 'right', color: 'var(--primary-dark)' }} className="vv-amount">{formatCurrency(debitSum)}</div>
                                    <div style={{ flex: 1, textAlign: 'right', color: 'var(--primary)' }} className="vv-amount">{formatCurrency(creditSum)}</div>
                                </div>
                            </div>
                        </div>
                    </>
                ) : (
                    <div className="text-center py-4 text-muted">Kunde inte ladda verifikatet.</div>
                )}
            </Modal.Body>
        </Modal>
    );
};

export default VatVerificationViewModal;
